/**
 * Immediately Invoked Function Expression (IIFE) to initialize the `zag` namespace
 * and its utilities, ensuring no global scope pollution.
 */

(() => {
  "use strict";

  /**
   * The root namespace for the `zag` library.
   * Provides utilities, DOM helpers, managers, and components.
   */
  window.zag = window.zag || {};

  /**
   * Initializes the `zag` library and dispatches a custom "zag:ready" event.
   * This function is called when the DOM is fully loaded.
   *
   * @function window.zag.init
   * @fires zag:ready - Custom event dispatched when the library is initialized.
   */
  window.zag.init = () => {
    window.zag.utils.documentScrollbar.observeWidth();

    window.zag.utils.initVideoLazyload();

    window.dispatchEvent(new CustomEvent("zag:ready"));
  };

  window.zag.utils = window.zag.utils || {};
  window.zag.dom = window.zag.dom || {};
  window.zag.managers = window.zag.managers || {};
  window.zag.components = window.zag.components || {};

  /**
   * Returns the root font size of the document in pixels.
   *
   * @function window.zag.utils.rootFontSize
   * @returns {number} - The root font size in pixels.
   */
  window.zag.utils.rootFontSize = () => {
    return parseFloat(getComputedStyle(document.documentElement).fontSize);
  };

  /**
   * Creates a throttled function that only invokes the provided callback at most once per animation frame.
   *
   * @function window.zag.utils.throttle
   * @param {Function} callback - The callback function to be throttled.
   * @returns {Function} - The throttled function.
   */
  window.zag.utils.throttle = (callback) => {
    let requestId = null;
    let lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  };

  /**
   * Creates a debounced function that delays invoking the provided callback function.
   *
   * @function window.zag.utils.debounce
   * @param {Function} callback - The callback function to be debounced.
   * @param {number} [delay=100] - The delay in milliseconds before invoking the callback function.
   * @returns {Function} - The debounced function.
   */
  window.zag.utils.debounce = (callback, delay = 100) => {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback(...args);
      }, delay);
    };
  };

  /**
   * A singleton instance of `ModifiersManager` for `document.documentElement`.
   * Provides methods to manage attribute-based modifiers on the `<html>` element.
   */
  Object.defineProperty(window.zag.utils, "documentModifiers", {
    get() {
      if (!this._documentModifiers) {
        this._documentModifiers = new window.zag.managers.ModifiersManager(
          document.documentElement
        );
      }
      return this._documentModifiers;
    },
  });

  /**
   * Namespace for managing scroll-related functionality on the `document.documentElement`.
   * Provides methods and properties to lock/unlock scrolling and manage scroll-related modifiers.
   *
   * @property {boolean} isLocked - Getter and setter for the "locked-scroll" modifier.
   */
  window.zag.utils.documentScroll = {
    /**
     * Checks if scrolling is locked by checking the "locked-scroll" modifier.
     * @returns {boolean} True if scrolling is locked, false otherwise.
     */
    get isLocked() {
      return window.zag.utils.documentModifiers.has("locked-scroll");
    },

    /**
     * Locks or unlocks scrolling by adding or removing the "locked-scroll" modifier.
     * @param {boolean} value - True to lock scrolling, false to unlock.
     */
    set isLocked(value) {
      if (value) {
        document.documentElement.style.scrollBehavior = "auto";
        document.documentElement.style.insetBlockStart = `-${window.scrollY}px`;

        window.zag.utils.documentModifiers.add("locked-scroll");
      } else {
        window.zag.utils.documentModifiers.remove("locked-scroll");

        window.scrollTo(
          0,
          -1 * parseInt(document.documentElement.style.insetBlockStart)
        );
        document.documentElement.style.insetBlockStart = null;
        document.documentElement.style.scrollBehavior = null;
      }
    },
  };

  /**
   * Namespace for managing the scrollbar width and related functionality on the `document.documentElement`.
   * Provides methods to calculate, update, and observe changes in the scrollbar width.
   *
   * @property {number} width - Getter for the current scrollbar width in pixels.
   *   - Dynamically calculates the scrollbar width as the difference between `window.innerWidth` and `document.documentElement.clientWidth`.
   * @method updateWidth - Updates the CSS variable `--scrollbar-width` with the current scrollbar width.
   * @method observeWidth - Starts observing changes in the document size and updates the scrollbar width dynamically.
   *   - Uses a `ResizeObserver` to monitor changes.
   *   - Returns the `ResizeObserver` instance.
   * @method disconnectObserver - Disconnects the `ResizeObserver` to stop observing changes in the document size.
   */
  window.zag.utils.documentScrollbar = (() => {
    let widthObserver = null;

    return {
      get width() {
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        return Math.max(scrollbarWidth, 0);
      },

      updateWidth() {
        document.documentElement.style.setProperty(
          "--scrollbar-width",
          `${this.width}px`
        );
      },
      observeWidth() {
        if (!widthObserver) {
          widthObserver = new ResizeObserver(() => {
            this.updateWidth();
          });
          widthObserver.observe(document.documentElement);
        }

        return widthObserver;
      },
      disconnectObserver() {
        if (widthObserver) {
          widthObserver.disconnect();
          widthObserver = null;
        }
      },
    };
  })();

  window.zag.utils.initVideoLazyload = () => {
    const videos = document.querySelectorAll(
      "video[data-modifiers*='lazyload']"
    );
    if (videos.length === 0) return;

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        if (entry.isIntersecting) {
          video.setAttribute("poster", video.getAttribute("data-poster") || "");
          video.removeAttribute("data-poster");

          Array.from(video.children).forEach((child) => {
            child.setAttribute("src", child.getAttribute("data-src") || "");
            child.removeAttribute("data-src");
          });

          video.load();
          videoObserver.unobserve(video);
        }
      });
    });

    videos.forEach((video) => {
      videoObserver.observe(video);
    });
  };

  /**
   * Fetches the cart and generates a unique bundle ID based on the provided baseBundleId.
   *
   * @function window.zag.utils.getUniqueBundleId
   * @param {string} baseBundleId - The base bundle id without count suffix.
   */
  window.zag.utils.getUniqueBundleId = async (baseBundleId) => {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
      });
      const cart = await response.json();

      const used = new Set();
      const strictPrefix = `${baseBundleId}_`;

      for (const item of cart.items || []) {
        const id = item?.properties?._bundleId;
        if (typeof id === "string" && id.startsWith(strictPrefix)) {
          const m = id.match(/_(\d+)$/);
          if (m) used.add(parseInt(m[1], 10));
        }
      }

      let i =
        (parseInt(window.localStorage.getItem("bundleIdCounter")) || 0) + 1;
      window.localStorage.setItem("bundleIdCounter", i);

      const suffix = String(i).padStart(2, "0");
      const id = `${baseBundleId}_${suffix}`;
      return id;
    } catch (error) {
      console.error("Error fetching cart for bundle ID check:", error);
      return `${baseBundleId}_01`;
    }
  };

  /**
   * Namespace for accessing common DOM elements in the application.
   *
   * @property {HTMLElement|null} pageContent - The main content area of the page.
   * @property {HTMLElement|null} header - The header element of the page.
   * @property {HTMLElement|null} footer - The footer element of the page.
   * @property {HTMLElement|null} cartDrawer - The cart drawer element.
   * @property {HTMLElement|null} modalManager - The modal manager element.
   * @property {HTMLElement|null} stickyManager - The sticky manager element.
   */
  window.zag.dom = {
    get pageContent() {
      const element = document.getElementById("page_content");
      if (!element) {
        console.warn("Element with ID 'page_content' not found.");
      }

      return element;
    },
    get header() {
      const element = document.getElementById("header");
      if (!element) {
        console.warn("Element with ID 'header' not found.");
      }

      return element;
    },
    get footer() {
      const element = document.getElementById("footer");
      if (!element) {
        console.warn("Element with ID 'footer' not found.");
      }

      return element;
    },
    get miniCart() {
      const element = document.getElementById("mini_cart");
      if (!element) {
        console.warn("Element with ID 'mini_cart' not found.");
      }

      return element;
    },
    get cart() {
      const element = document.getElementById("cart");
      if (!element) {
        console.warn("Element with ID 'cart' not found.");
      }

      return element;
    },
    get search() {
      const element = document.getElementById("search");
      if (!element) {
        console.warn("Element with ID 'search' not found.");
      }

      return element;
    },
    get modalManager() {
      const element = document.getElementById("modal_manager");
      if (!element) {
        console.warn("Element with ID 'modal_manager' not found.");
      }

      return element;
    },
    get stickyManager() {
      const element = document.getElementById("sticky_manager");
      if (!element) {
        console.warn("Element with ID 'sticky_manager' not found.");
      }

      return element;
    },
  };

  /**
   * Manages event listeners for easy addition and removal.
   * Provides centralized tracking of events to prevent memory leaks.
   */
  window.zag.managers.EventsManager = class {
    constructor() {
      this._listeners = new Map();
    }

    /**
     * Adds an event listener and manages it for easy removal.
     *
     * @param {EventTarget} element - The element to attach the event listener to.
     * @param {string} type - The type of event to listen for.
     * @param {Function} handler - The function to be called when the event occurs.
     * @param {Object|boolean} [options={}] - Options for the event listener.
     * @returns {this} - For method chaining.
     */
    add = (element, type, handler, options = {}) => {
      try {
        if (!(element instanceof EventTarget)) {
          throw new Error("Invalid element provided to add");
        }

        element.addEventListener(type, handler, options);

        if (!this._listeners.has(element)) {
          this._listeners.set(element, new Set());
        }
        this._listeners.get(element).add({ type, handler, options });

        return this;
      } catch (error) {
        console.error(error.message, this);
        return this;
      }
    };

    /**
     * Checks if a specific event listener exists.
     *
     * @param {EventTarget} element - The element to check.
     * @param {string} type - The type of event.
     * @param {Function} handler - The handler function.
     * @returns {boolean} - Whether the listener exists.
     */
    has = (element, type, handler) => {
      if (!this._listeners.has(element)) return false;

      const listeners = this._listeners.get(element);
      for (const listener of listeners) {
        if (listener.type === type && listener.handler === handler) {
          return true;
        }
      }
      return false;
    };

    /**
     * Removes a listener for the event.
     *
     * @param {EventTarget} element - The element to remove the listener from.
     * @param {string} type - The type of event.
     * @param {Function} handler - The handler function to remove.
     * @returns {this} - For method chaining.
     */
    remove = (element, type, handler) => {
      if (!(element instanceof EventTarget)) {
        console.error("Invalid element provided to remove");
        return this;
      }
      if (!this._listeners.has(element)) return this;

      const elementListeners = this._listeners.get(element);

      elementListeners.forEach((listener) => {
        if (listener.type === type && listener.handler === handler) {
          element.removeEventListener(type, handler, listener.options);
          elementListeners.delete(listener);
        }
      });

      if (elementListeners.size === 0) {
        this._listeners.delete(element);
      }

      return this;
    };

    /**
     * Removes all event listeners for a specific element.
     *
     * @param {EventTarget} element - The element to remove all listeners from.
     * @returns {this} - For method chaining.
     */
    removeFrom = (element) => {
      if (!this._listeners.has(element)) return this;

      const elementListeners = this._listeners.get(element);
      elementListeners.forEach(({ type, handler, options }) => {
        element.removeEventListener(type, handler, options);
      });

      this._listeners.delete(element);

      return this;
    };

    /**
     * Removes all managed event listeners.
     *
     * @returns {this} - For method chaining.
     */
    clear = () => {
      for (const [element, listeners] of this._listeners) {
        listeners.forEach(({ type, handler, options }) => {
          element.removeEventListener(type, handler, options);
        });
      }
      this._listeners.clear();

      return this;
    };

    /**
     * Gets the count of managed listeners.
     *
     * @returns {number} - The total number of managed listeners.
     */
    getCount = () => {
      let count = 0;
      for (const listeners of this._listeners.values()) {
        count += listeners.size;
      }
      return count;
    };

    /**
     * Gets all managed listeners for debugging purposes.
     *
     * @returns {Array} - An array of all managed listeners.
     */
    getAllListeners = () => {
      const allListeners = [];
      for (const [element, listeners] of this._listeners) {
        listeners.forEach((listener) => {
          allListeners.push({ element, ...listener });
        });
      }
      return allListeners;
    };
  };

  /**
   * Manages observers (Resize, Intersection, Mutation) for easy addition and cleanup.
   * Provides centralized tracking of observers to prevent memory leaks.
   */
  window.zag.managers.ObserversManager = class {
    constructor() {
      this._observers = new Map();
    }

    /**
     * Creates and adds an observer for the specified element.
     *
     * @param {Element} element - The element to observe.
     * @param {string} type - The type of observer ('resize', 'intersection', or 'mutation').
     * @param {Function} handler - The function to be called when changes are observed.
     * @param {Object} [options={}] - Options for the observer.
     * @returns {ResizeObserver|IntersectionObserver|MutationObserver|undefined} - The created observer.
     */
    add = (element, type, handler, options = {}) => {
      try {
        if (!(element instanceof Element)) {
          throw new Error("Invalid element provided to add");
        } else if (!["resize", "intersection", "mutation"].includes(type)) {
          throw new Error("Invalid observer type provided");
        }

        let observer;

        switch (type) {
          case "resize":
            observer = new ResizeObserver(handler);
            observer.observe(element, options);
            break;
          case "intersection":
            observer = new IntersectionObserver(handler, options);
            observer.observe(element);
            break;
          case "mutation":
            observer = new MutationObserver(handler);
            observer.observe(element, options);
            break;
        }

        if (!this._observers.has(element)) {
          this._observers.set(element, new Set());
        }

        this._observers.get(element).add({ type, handler, observer });

        return observer;
      } catch (error) {
        console.error(error);
        return undefined;
      }
    };

    /**
     * Convenience method to add a resize observer.
     *
     * @param {Element} element - The element to observe resize events on.
     * @param {Function} handler - The function to be called when resize occurs.
     * @param {Object} [options={}] - Options for the resize observer.
     * @returns {ResizeObserver|undefined} - The created observer.
     */
    resize = (element, handler, options = {}) =>
      this.add(element, "resize", handler, options);

    /**
     * Convenience method to add an intersection observer.
     *
     * @param {Element} element - The element to observe intersection events on.
     * @param {Function} handler - The function to be called when intersection changes.
     * @param {Object} [options={}] - Options for the intersection observer.
     * @returns {IntersectionObserver|undefined} - The created observer.
     */
    intersection = (element, handler, options = {}) =>
      this.add(element, "intersection", handler, options);

    /**
     * Convenience method to add a mutation observer.
     *
     * @param {Element} element - The element to observe mutations on.
     * @param {Function} handler - The function to be called when mutations occur.
     * @param {Object} [options={}] - Options for the mutation observer.
     * @returns {MutationObserver|undefined} - The created observer.
     */
    mutation = (element, handler, options = {}) =>
      this.add(element, "mutation", handler, options);

    /**
     * Checks if a specific observer exists.
     *
     * @param {Element} element - The element to check.
     * @param {string} type - The type of observer.
     * @param {Function} handler - The handler function.
     * @returns {boolean} - Whether the observer exists.
     */
    has = (element, type, handler) => {
      if (!this._observers.has(element)) return false;

      const observers = this._observers.get(element);
      for (const entry of observers) {
        if (entry.type === type && entry.handler === handler) {
          return true;
        }
      }
      return false;
    };

    /**
     * Removes a specific observer.
     *
     * @param {Element} element - The element to remove the observer from.
     * @param {string} type - The type of observer to remove.
     * @param {Function} handler - The handler function to remove.
     * @returns {this} - For method chaining.
     */
    remove = (element, type, handler) => {
      if (!(element instanceof Element)) {
        console.error("Invalid element provided to remove");
        return this;
      }
      if (!this._observers.has(element)) return this;

      const elementObservers = this._observers.get(element);

      elementObservers.forEach((entry) => {
        if (entry.type === type && entry.handler === handler) {
          entry.observer.disconnect();
          elementObservers.delete(entry);
        }
      });

      if (elementObservers.size === 0) {
        this._observers.delete(element);
      }

      return this;
    };

    /**
     * Removes all observers for a specific element.
     *
     * @param {Element} element - The element to remove all observers from.
     * @returns {this} - For method chaining.
     */
    removeFrom = (element) => {
      const elementObservers = this._observers.get(element);
      if (!elementObservers) return this;

      elementObservers.forEach(({ observer }) => {
        observer.disconnect();
      });

      this._observers.delete(element);
      return this;
    };

    /**
     * Removes all observers.
     *
     * @returns {this} - For method chaining.
     */
    clear = () => {
      for (const observers of this._observers.values()) {
        observers.forEach(({ observer }) => {
          observer.disconnect();
        });
      }

      this._observers.clear();
      return this;
    };

    /**
     * Gets the count of managed observers.
     *
     * @returns {number} - The total number of managed observers.
     */
    getCount = () => {
      let count = 0;
      for (const observers of this._observers.values()) {
        count += observers.size;
      }
      return count;
    };

    /**
     * Gets all managed observers for debugging purposes.
     *
     * @returns {Array} - An array of all managed observers.
     */
    getAllObservers = () => {
      const allObservers = [];
      for (const [element, observers] of this._observers) {
        observers.forEach((entry) => {
          allObservers.push({ element, ...entry });
        });
      }
      return allObservers;
    };
  };

  /**
   * Manages caching of values for improved performance.
   * Provides a flexible, type-based caching system with selective invalidation and optional expiration.
   */
  window.zag.managers.CacheManager = class {
    constructor() {
      this.caches = new Map();
      this.caches.set("dom", new Map());
    }

    /**
     * Gets a cached value or computes and caches it if not present.
     *
     * @param {string} type - The cache type.
     * @param {string} key - The cache key.
     * @param {Function} computeFunction - Function to compute the value if not cached.
     * @returns {*} - The cached or computed value.
     */
    get = (type, key, computeFunction) => {
      const cache = this.ensureCache(type);

      if (cache.has(key)) {
        const entry = cache.get(key);
        if (entry.expiresAt && Date.now() > entry.expiresAt) {
          cache.delete(key); // Remove expired entry
        } else {
          return entry.value;
        }
      }

      const value = computeFunction();
      this.set(type, key, value);
      return value;
    };

    /**
     * Sets a value in the cache with optional expiration.
     *
     * @param {string} type - The cache type.
     * @param {string} key - The cache key.
     * @param {*} value - The value to cache.
     * @param {number|null} [ttl=null] - Time-to-live in milliseconds (optional).
     * @returns {*} - The cached value.
     */
    set = (type, key, value, ttl = null) => {
      const cache = this.ensureCache(type);
      const entry = { value, expiresAt: ttl ? Date.now() + ttl : null };
      cache.set(key, entry);
      return value;
    };

    /**
     * Checks if a value exists in the cache.
     *
     * @param {string} type - The cache type.
     * @param {string} key - The cache key.
     * @returns {boolean} - Whether the value is cached.
     */
    has = (type, key) => {
      if (!this.caches.has(type)) return false;
      return this.caches.get(type).has(key);
    };

    /**
     * Clears specific cache types or all caches.
     *
     * @param {string|string[]} [types] - The cache types to clear. If omitted, clears all caches.
     * @returns {this} - For method chaining.
     */
    clear = (types) => {
      if (types) {
        [].concat(types).forEach((type) => {
          if (this.caches.has(type)) {
            this.caches.get(type).clear();
          }
        });
      } else {
        this.caches.forEach((cache) => cache.clear());
      }

      return this;
    };

    /**
     * Ensures a cache exists for the given type and returns it.
     *
     * @param {string} type - The cache type.
     * @returns {Map<string, any>} - The cache Map.
     */
    ensureCache = (type) => {
      if (typeof type !== "string") {
        throw new Error("Cache type must be a string");
      }
      if (!this.caches.has(type)) {
        this.caches.set(type, new Map());
      }
      return this.caches.get(type);
    };

    /**
     * Gets all available cache types.
     *
     * @returns {string[]} - Array of cache type names.
     */
    getTypes = () => {
      return Array.from(this.caches.keys());
    };

    /**
     * Removes cache entries that match a predicate function.
     *
     * @param {string|string[]} types - The cache types to check.
     * @param {Function} predicate - Function that returns true for entries to remove.
     * @returns {number} - Number of entries removed.
     */
    removeIf = (types, predicate) => {
      let removedCount = 0;
      const typeList = [].concat(types);

      typeList.forEach((type) => {
        if (!this.caches.has(type)) return;

        const cache = this.caches.get(type);
        for (const [key, value] of cache.entries()) {
          if (predicate(value, key, type)) {
            cache.delete(key);
            removedCount++;
          }
        }
      });

      return removedCount;
    };

    /**
     * Gets the size of a specific cache or all caches combined.
     *
     * @param {string} [type] - The cache type. If omitted, returns the total size of all caches.
     * @returns {number} - The size of the cache(s).
     */
    getSize = (type) => {
      if (type) {
        const cache = this.caches.get(type);
        return cache ? cache.size : 0;
      }
      return Array.from(this.caches.values()).reduce(
        (total, cache) => total + cache.size,
        0
      );
    };
  };

  /**
   * Manages attribute-based modifiers for HTML elements.
   * Provides methods to check, add, remove, toggle, and replace modifiers stored in a data attribute.
   *
   * @class ModifiersManager
   * @param {HTMLElement} element - The element to manage modifiers for.
   */
  window.zag.managers.ModifiersManager = class {
    constructor(element) {
      this.element = element;
      this.attributeName = "data-modifiers";
    }

    /**
     * Gets the list of all modifiers on the element.
     * @returns {string[]} Array of modifier strings.
     */
    list = () => {
      if (!this.element.hasAttribute(this.attributeName)) return [];
      return this.element
        .getAttribute(this.attributeName)
        .split(",")
        .map((modifier) => modifier.trim())
        .filter(Boolean);
    };

    /**
     * Checks if the element has a specific modifier.
     * @param {string} modifier - The modifier to check for.
     * @returns {boolean} True if the element has the modifier.
     */
    has = (modifier) => {
      return this.list().includes(modifier);
    };

    /**
     * Adds one or more modifiers to the element.
     * @param {...string} modifiers - Modifiers to add.
     * @returns {this} For method chaining.
     */
    add = (...modifiers) => {
      const current = this.list();
      const newModifiers = modifiers.filter((m) => !current.includes(m));

      if (newModifiers.length === 0) return this;

      const updated = [...current, ...newModifiers];
      this.element.setAttribute(this.attributeName, updated.join(", "));

      return this;
    };

    /**
     * Removes one or more modifiers from the element.
     * @param {...string} modifiers - Modifiers to remove.
     * @returns {this} For method chaining.
     */
    remove = (...modifiers) => {
      const current = this.list();
      const updated = current.filter((m) => !modifiers.includes(m));

      if (updated.length === 0) {
        this.element.removeAttribute(this.attributeName);
      } else {
        this.element.setAttribute(this.attributeName, updated.join(", "));
      }

      return this;
    };

    /**
     * Toggles one or more modifiers on the element.
     * @param {...string} modifiers - Modifiers to toggle.
     * @returns {this} For method chaining.
     */
    toggle = (...modifiers) => {
      const current = this.list();
      const updated = new Set(current);

      modifiers.forEach((modifier) => {
        if (updated.has(modifier)) {
          updated.delete(modifier);
        } else {
          updated.add(modifier);
        }
      });

      if (updated.size === 0) {
        this.element.removeAttribute(this.attributeName);
      } else {
        this.element.setAttribute(
          this.attributeName,
          Array.from(updated).join(", ")
        );
      }

      return this;
    };

    /**
     * Sets a single modifier based on a condition
     * @param {string} modifier - The modifier to set
     * @param {boolean} condition - Whether to add (true) or remove (false) the modifier
     * @returns {this} For method chaining
     */
    set = (modifier, condition) => {
      return condition ? this.add(modifier) : this.remove(modifier);
    };

    /**
     * Clears all modifiers.
     * @returns {this} For method chaining.
     */
    clear = () => {
      this.element.removeAttribute(this.attributeName);
      return this;
    };

    /**
     * Gets the count of modifiers on the element.
     * @returns {number} The number of modifiers.
     */
    getCount = () => {
      return this.list().length;
    };
  };

  /**
   * Base class for custom elements in the `zag` library.
   * Provides integrated utilities for event management, observers, caching, and modifiers.
   */
  window.zag.components.ZagComponent = class extends HTMLElement {
    constructor() {
      super();

      try {
        this.events = new window.zag.managers.EventsManager();
        this.observers = new window.zag.managers.ObserversManager();
        this.cache = new window.zag.managers.CacheManager();
        this.modifiers = new window.zag.managers.ModifiersManager(this);
      } catch (error) {
        console.error("Error initializing ZagComponent utilities:", error);
      }
    }

    disconnectedCallback() {
      if (this.events) this.events.clear();
      if (this.observers) this.observers.clear();
      if (this.cache) this.cache.clear();
      if (this.modifiers) this.modifiers.clear();
    }

    get section() {
      return this.cache.get("dom", "section", () => {
        const section = this.closest(".shopify-section");
        if (!section) {
          console.warn("No parent section found for ZagComponent");
        }

        return section;
      });
    }
  };
  window.customElements.define(
    "zag-section",
    window.zag.components.ZagComponent
  );

  window.zag.components.ZagSlider = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();

      this.activeIndex = 0;

      this.dragState = {
        isDragging: false,
        top: 0,
        left: 0,
        x: 0,
        y: 0,
        startTime: 0,
      };

      this.nearInlineStart = true;
      this.nearInlineEnd = false;

      this.preventClick = false;

      this.autoScrollTimer = null;
      this.autoScrollPaused = false;
    }

    connectedCallback() {
      try {
        if (this.slideList.length === 0) {
          throw new Error("Slider must have at least one slide");
        }

        this.setupEventListeners();

        this.updateSliderLayout();

        this.initializeActiveSlide();

        if (this.modifiers.has("log-events")) {
          this.logEvents();
        }
      } catch (error) {
        console.error("Error initializing ZAG slider:", error, this);
      }
    }

    get slideList() {
      return this.cache.get("slider", "list", () => {
        return Array.from(this.children).filter(
          (child) => !child.matches("zag-slider-control")
        );
      });
    }

    get direction() {
      return this.cache.get("slider", "direction", () => {
        const sliderDirection = getComputedStyle(this)
          .getPropertyValue("--slider-direction")
          .trim();

        if (sliderDirection === "") return "row";

        return ["row", "column"].includes(sliderDirection)
          ? sliderDirection
          : "row";
      });
    }

    get alignment() {
      return this.cache.get("slider", "alignment", () => {
        const sliderAlignment = getComputedStyle(this)
          .getPropertyValue("--slider-alignment")
          .trim();

        if (sliderAlignment === "") return "start";

        return ["start", "center", "end"].includes(sliderAlignment)
          ? sliderAlignment
          : "start";
      });
    }

    get gap() {
      return this.cache.get("slider", "gap", () => {
        const gap = getComputedStyle(this)
          .getPropertyValue("--slider-gap")
          .trim();

        if (gap === "") return 0;

        if (gap.endsWith("rem")) {
          return window.zag.utils.rootFontSize * parseFloat(gap);
        } else if (gap.endsWith("px")) {
          return parseFloat(gap);
        }

        return 0;
      });
    }

    get paddingStart() {
      return this.cache.get("slider", "paddingStart", () => {
        const startPadding = getComputedStyle(this)
          .getPropertyValue("--slider-padding-start")
          .trim();

        if (startPadding === "") return 0;

        if (startPadding.endsWith("rem")) {
          return window.zag.utils.rootFontSize * parseFloat(startPadding);
        } else if (startPadding.endsWith("px")) {
          return parseFloat(startPadding);
        }

        return 0;
      });
    }

    get paddingEnd() {
      return this.cache.get("slider", "paddingEnd", () => {
        const startPadding = getComputedStyle(this)
          .getPropertyValue("--slider-padding-end")
          .trim();

        if (startPadding === "") return 0;

        if (startPadding.endsWith("rem")) {
          return window.zag.utils.rootFontSize * parseFloat(startPadding);
        } else if (startPadding.endsWith("px")) {
          return parseFloat(startPadding);
        }

        return 0;
      });
    }

    get slidesPerView() {
      return this.cache.get("slider", "slidesPerView", () => {
        const slidesPerView = getComputedStyle(this)
          .getPropertyValue("--slides-per-view")
          .trim();

        if (slidesPerView === "" || slidesPerView === "auto") return "auto";

        const parsedValue = parseInt(slidesPerView);
        return isNaN(parsedValue) ? "auto" : parsedValue;
      });
    }

    get scrollBehavior() {
      return this.cache.get("slider", "scrollBehavior", () => {
        const scrollBehavior = getComputedStyle(this)
          .getPropertyValue("--scroll-behavior")
          .trim();

        if (scrollBehavior === "") return "instant";

        return ["smooth", "instant"].includes(scrollBehavior)
          ? scrollBehavior
          : "instant";
      });
    }

    get drag() {
      return this.cache.get(
        "slider",
        "drag",
        () => !this.modifiers.has("no-drag")
      );
    }

    get loop() {
      return this.cache.get("slider", "loop", () => this.modifiers.has("loop"));
    }

    get updateSource() {
      return this.cache.get("slider", "updateSource", () =>
        this.modifiers.has("update-on-change") ? "change" : "settle"
      );
    }

    get initialSlideIndex() {
      return this.cache.get("slider", "initialSlideIndex", () => {
        const initialIndex = this.slideList.findIndex((slide) => {
          return slide.hasAttribute("data-initial-slide");
        });

        return initialIndex !== -1 ? initialIndex : 0;
      });
    }

    get autoScroll() {
      return this.cache.get("slider", "autoScroll", () => {
        return this.modifiers.has("auto-scroll");
      });
    }

    get autoScrollInterval() {
      return this.cache.get("slider", "autoScrollInterval", () => {
        const interval = getComputedStyle(this)
          .getPropertyValue("--auto-scroll-interval")
          .trim();

        if (interval === "") return 5000;

        const parsedValue = parseInt(interval);
        return isNaN(parsedValue) ? 5000 : Math.max(1000, parsedValue);
      });
    }

    get autoScrollPauseOnHover() {
      return this.cache.get("slider", "autoScrollPauseOnHover", () => {
        return !this.modifiers.has("no-pause-on-hover");
      });
    }

    setupEventListeners = () => {
      this.events.add(
        this,
        "scroll",
        window.zag.utils.throttle(this.onScroll),
        {
          passive: true,
        }
      );
      this.events.add(
        this,
        "scroll",
        window.zag.utils.debounce(this.onScrollEnd, 200),
        {
          passive: true,
        }
      );

      this.observers.resize(this, this.onResize);
      this.events.add(this, "keydown", this.onKeyDown);

      if (this.drag) {
        const mediaQuery = window.matchMedia("(pointer: fine)");
        this.events.add(mediaQuery, "change", this.updateDragBehavior);
        this.updateDragBehavior(mediaQuery);
      }

      if (this.autoScroll) {
        this.startAutoScroll();

        if (this.autoScrollPauseOnHover) {
          this.events.add(
            this.parentElement,
            "mouseenter",
            this.pauseAutoScroll
          );
          this.events.add(
            this.parentElement,
            "mouseleave",
            this.resumeAutoScroll
          );
        }

        this.events.add(this, "zag-slider:drag-start", this.pauseAutoScroll);
        this.events.add(this, "keydown", this.onKeyDownWithAutoScroll);
      }
    };

    initializeActiveSlide = () => {
      if (this.initialSlideIndex !== this.activeIndex) {
        this.select(this.initialSlideIndex, "instant");
      } else {
        this.activeIndex = this.calculateClosestIndex();
        this.onScroll();
      }
    };

    logEvents = () => {
      const events = [
        "zag-slider:select",
        "zag-slider:previous",
        "zag-slider:next",
        "zag-slider:drag-start",
        "zag-slider:drag-end",
        "zag-slider:change",
        "zag-slider:settle",
        "zag-slider:edge-nearing",
        "zag-slider:edge-leaving",
      ];

      events.forEach((eventName) => {
        this.events.add(this, eventName, (event) => {
          event.detail
            ? console.log(`[ZagSlider] ${eventName}:`, event.detail)
            : console.log(`[ZagSlider] ${eventName}`);
        });
      });
    };

    /**
     * Navigates to the previous slide
     * @param {string} [behavior=this.scrollBehavior] - The scroll behavior
     * @returns {this} - For method chaining
     */
    previous = (behavior = this.scrollBehavior) => {
      if (!this.loop && this.nearInlineStart) return this;

      this.dispatchEvent(new CustomEvent("zag-slider:previous"));

      if (this.loop && this.nearInlineStart) {
        this.select(this.slideList.length - 1, behavior);
      } else {
        this.select(Math.max(this.activeIndex - 1, 0), behavior);
      }
    };

    /**
     * Navigates to the next slide
     * @param {string} [behavior=this.scrollBehavior] - The scroll behavior
     * @returns {this} - For method chaining
     */
    next = (behavior = this.scrollBehavior) => {
      if (!this.loop && this.nearInlineEnd) return this;

      this.dispatchEvent(new CustomEvent("zag-slider:next"));

      if (this.loop && this.nearInlineEnd) {
        this.select(0, behavior);
      } else {
        this.select(
          Math.min(this.activeIndex + 1, this.slideList.length - 1),
          behavior
        );
      }

      return this;
    };

    /**
     * Selects a specific slide by index
     * @param {number} index - The slide index
     * @param {string} [behavior=this.scrollBehavior] - The scroll behavior
     * @returns {this} - For method chaining
     */
    select = (index, behavior = this.scrollBehavior) => {
      if (this.offsetWidth === 0) return this;

      this.dispatchEvent(
        new CustomEvent("zag-slider:select", {
          detail: { slide: this.slideList[index], index },
        })
      );

      this.activeIndex = index;

      const scrollOptions = {
        behavior,
      };

      this.direction === "row"
        ? (scrollOptions.left = this.calculateSlideTargetScroll(
            this.slideList[index]
          ))
        : (scrollOptions.top = this.calculateSlideTargetScroll(
            this.slideList[index]
          ));

      this.scrollTo(scrollOptions);

      return this;
    };

    calculateSlideTargetScroll = (slide) => {
      if (!slide) return 0;

      const direction = this.direction;
      const alignment = this.alignment;
      const paddingStart = this.paddingStart;
      const paddingEnd = this.paddingEnd;

      const scrollSize =
        direction === "row" ? this.scrollWidth : this.scrollHeight;
      const offsetSize =
        direction === "row" ? this.offsetWidth : this.offsetHeight;

      let targetScroll;

      if (alignment === "center") {
        const slideCenter =
          this.direction === "row"
            ? slide.offsetLeft + slide.offsetWidth / 2
            : slide.offsetTop + slide.offsetHeight / 2;

        targetScroll = slideCenter - offsetSize / 2;
      } else if (alignment === "start") {
        targetScroll =
          this.direction === "row"
            ? slide.offsetLeft - paddingStart
            : slide.offsetTop - paddingStart;
      } else if (alignment === "end") {
        targetScroll =
          this.direction === "row"
            ? slide.offsetLeft + slide.offsetWidth - offsetSize + paddingEnd
            : slide.offsetTop + slide.offsetHeight - offsetSize + paddingEnd;
      }

      return Math.max(0, Math.min(targetScroll, scrollSize - offsetSize));
    };

    updateSliderLayout = () => {
      this.cache.clear(["slider"]);

      this.updateActiveIndex();
    };

    updateActiveIndex = () => {
      const closestIndex = this.calculateClosestIndex();

      if (closestIndex !== this.activeIndex) {
        this.activeIndex = closestIndex;

        this.dispatchEvent(
          new CustomEvent("zag-slider:change", {
            detail: {
              slide: this.slideList[this.activeIndex],
              index: this.activeIndex,
            },
          })
        );
      }
    };

    calculateClosestIndex = () => {
      const referencePoint = this.getScrollReferencePoint();

      let closestIndex = 0;
      let minDistance = Infinity;

      this.slideList.forEach((slide, index) => {
        const slideReferencePoint = this.getSlideReferencePoint(slide);

        const distance = Math.abs(slideReferencePoint - referencePoint);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    getScrollReferencePoint = () => {
      const direction = this.direction;
      const alignment = this.alignment;
      const paddingStart = this.paddingStart;
      const paddingEnd = this.paddingEnd;
      const scroll = direction === "row" ? this.scrollLeft : this.scrollTop;
      const offsetSize =
        direction === "row" ? this.offsetWidth : this.offsetHeight;

      if (alignment === "center") {
        return scroll + offsetSize / 2;
      } else if (alignment === "start") {
        return scroll + paddingStart;
      } else if (alignment === "end") {
        return scroll + offsetSize - paddingEnd;
      }
    };

    getSlideReferencePoint = (slide) => {
      if (!slide) return 0;

      const direction = this.direction;
      const alignment = this.alignment;

      const scroll = direction === "row" ? slide.offsetLeft : slide.offsetTop;
      const offsetSize =
        direction === "row" ? slide.offsetWidth : slide.offsetHeight;

      if (alignment === "center") {
        return scroll + offsetSize / 2;
      } else if (alignment === "start") {
        return scroll;
      } else {
        return scroll + offsetSize;
      }
    };

    updateDragBehavior = (mediaQuery) => {
      const hasFinePointer = mediaQuery.matches || mediaQuery === true;

      if (hasFinePointer && this.drag) {
        this.events.add(this, "pointerdown", this.onPointerDown);
      } else {
        this.events.remove(this, "pointerdown", this.onPointerDown);
      }
    };

    onScroll = () => {
      const rowSlider = this.direction === "row";

      const scroll = rowSlider ? this.scrollLeft : this.scrollTop;
      const scrollSize = rowSlider ? this.scrollWidth : this.scrollHeight;
      const viewportSize = rowSlider ? this.clientWidth : this.clientHeight;

      const distanceToEnd = scrollSize - viewportSize - scroll;
      const scrollEdgeThreshold = 50;

      this.checkEdgeState("start", scroll, scrollEdgeThreshold);
      this.checkEdgeState("end", distanceToEnd, scrollEdgeThreshold);

      this.updateActiveIndex();
    };

    checkEdgeState = (edge, distance, threshold) => {
      const isStart = edge === "start";
      const nearEdgeFlag = isStart ? "nearInlineStart" : "nearInlineEnd";

      if (distance < threshold && !this[nearEdgeFlag]) {
        this.dispatchEvent(
          new CustomEvent("zag-slider:edge-nearing", { detail: edge })
        );
        this[nearEdgeFlag] = true;
      } else if (distance >= threshold && this[nearEdgeFlag]) {
        this.dispatchEvent(
          new CustomEvent("zag-slider:edge-leaving", { detail: edge })
        );
        this[nearEdgeFlag] = false;
      }
    };

    onScrollEnd = () => {
      this.dispatchEvent(
        new CustomEvent("zag-slider:settle", {
          detail: {
            slide: this.slideList[this.activeIndex],
            index: this.activeIndex,
          },
        })
      );
    };

    onResize = () => {
      this.cache.clear(["slider"]);

      this.updateSliderLayout();

      if (this.autoScroll && this.autoScrollTimer) {
        this.startAutoScroll();
      }
    };

    onKeyDown = (event) => {
      if (
        document.activeElement !== this &&
        !this.contains(document.activeElement)
      ) {
        return;
      }

      const keyActions = {
        ArrowLeft: () => this.previous(),
        ArrowUp: () => this.previous(),
        ArrowRight: () => this.next(),
        ArrowDown: () => this.next(),
        Home: () => this.select(0),
        End: () => this.select(this.slideList.length - 1),
      };

      const action = keyActions[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    };

    onPointerDown = (event) => {
      event.preventDefault();

      this.dragState = {
        isDragging: true,
        top: this.scrollTop,
        left: this.scrollLeft,
        x: event.clientX,
        y: event.clientY,
        startTime: Date.now(),
      };

      this.preventClick = false;

      this.dispatchEvent(new CustomEvent("zag-slider:drag-start"));

      this.events.add(document, "pointermove", this.onPointerMove, {
        passive: false,
      });
      this.events.add(document, "pointerup", this.onPointerUp);

      this.events.add(this, "click", this.onClick, { capture: true });
    };

    onPointerMove = (event) => {
      if (!this.dragState.isDragging) return;

      event.preventDefault();

      const dx = event.clientX - this.dragState.x;
      const dy = event.clientY - this.dragState.y;

      // Set click prevention if movement exceeds threshold
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        this.preventClick = true;
      }

      const scrollOptions = { behavior: "instant" };
      this.direction === "row"
        ? (scrollOptions.left = this.dragState.left - dx)
        : (scrollOptions.top = this.dragState.top - dy);

      this.scrollTo(scrollOptions);
    };

    onPointerUp = (event) => {
      if (!this.dragState.isDragging) return;

      this.dragState.isDragging = false;

      const delta =
        this.direction === "row"
          ? event.clientX - this.dragState.x
          : event.clientY - this.dragState.y;

      const elapsed = Date.now() - this.dragState.startTime;
      const velocity = Math.abs(delta) / elapsed;

      // If we have significant velocity and displacement, move to the next/previous slide
      if (Math.abs(delta) > 50 && velocity > 0.5) {
        if (delta > 0) {
          this.previous();
        } else {
          this.next();
        }
      } else {
        this.select(this.activeIndex);
      }

      this.dispatchEvent(new CustomEvent("zag-slider:drag-end"));

      this.events.remove(document, "pointermove", this.onPointerMove);
      this.events.remove(document, "pointerup", this.onPointerUp);

      setTimeout(() => {
        this.events.remove(this, "click", this.onClick, {
          capture: true,
        });
      }, 100);
    };

    onClick = (event) => {
      if (this.preventClick) {
        event.preventDefault();
        event.stopPropagation();
        this.preventClick = false;
      }
    };

    startAutoScroll = () => {
      if (!this.autoScroll) return;

      this.stopAutoScroll();
      this.autoScrollTimer = setInterval(() => {
        if (!this.autoScrollPaused) {
          this.next();
        }
      }, this.autoScrollInterval);
    };

    stopAutoScroll = () => {
      if (this.autoScrollTimer) {
        clearInterval(this.autoScrollTimer);
        this.autoScrollTimer = null;
      }
    };

    pauseAutoScroll = () => {
      this.autoScrollPaused = true;
    };

    resumeAutoScroll = () => {
      this.autoScrollPaused = false;
    };

    onKeyDownWithAutoScroll = (event) => {
      this.pauseAutoScroll();

      setTimeout(() => {
        this.resumeAutoScroll();
      }, this.autoScrollInterval);

      this.onKeyDown(event);
    };
  };
  if (!window.customElements.get("zag-slider")) {
    window.customElements.define("zag-slider", window.zag.components.ZagSlider);
  }

  window.zag.components.ZagSliderControl = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();
    }

    connectedCallback() {
      try {
        if (!this.slider) {
          throw new Error(
            "ZAG slider control must be linked to a slider using aria-controls"
          );
        }

        if (this.type === "no-action") {
          throw new Error(
            "ZAG slider control must have a valid action modifier"
          );
        }

        this.events.add(this, "click", this.onClick);
        if (this.slider.updateSource === "change") {
          this.events.add(
            this.slider,
            "zag-slider:change",
            this.onSliderUpdate
          );
        } else {
          this.events.add(
            this.slider,
            "zag-slider:settle",
            this.onSliderUpdate
          );
        }
      } catch (error) {
        console.error("Error initializing ZAG slider control:", error, this);
      }
    }

    get slider() {
      return this.cache.get("dom", "slider", () => {
        const sliderId = this.getAttribute("aria-controls");
        return document.getElementById(sliderId);
      });
    }

    get actionModifier() {
      return this.cache.get("control", "actionModifier", () => {
        return this.modifiers.list().find((modifier) => {
          return modifier.startsWith("action:");
        });
      });
    }

    get target() {
      return this.cache.get("control", "target", () => {
        return this.actionModifier.split(":").at(-1) || "none";
      });
    }

    get type() {
      return this.cache.get("control", "type", () => {
        if (this.target === "previous" || this.target === "next") {
          return "button";
        }

        const parsedTarget = parseInt(this.target, 10);
        if (!isNaN(parsedTarget) && parsedTarget >= 0) {
          return "bullet";
        }

        return "no-action";
      });
    }

    onClick = (event) => {
      event.preventDefault();

      if (this.type === "bullet") {
        const targetIndex =
          parseInt(this.actionModifier.split(":").at(-1), 10) - 1;
        this.slider.select(targetIndex, this.slider.scrollBehavior);
      } else if (this.type === "button") {
        if (this.target === "previous") {
          this.slider.previous();
        } else if (this.target === "next") {
          this.slider.next();
        }
      }
    };

    get bulletSiblings() {
      return this.cache.get("bullet", "siblings", () => {
        if (this.type !== "bullet" || !this.parentElement) return [];

        return Array.from(this.parentElement.children).filter(
          (child) =>
            child !== this &&
            child.matches("zag-slider-control") &&
            child.modifiers &&
            child.modifiers
              .list()
              .some(
                (modifier) =>
                  modifier.startsWith("action:") &&
                  !isNaN(parseInt(modifier.split(":").at(-1), 10))
              )
        );
      });
    }

    get shouldCenterBullet() {
      return this.cache.get("bullet", "shouldCenter", () => {
        return this.type === "bullet" && this.bulletSiblings.length > 0;
      });
    }

    centerBulletInParent = () => {
      if (!this.shouldCenterBullet || !this.parentElement) return;

      const parent = this.parentElement;

      if (parent.scrollWidth !== parent.clientWidth) {
        parent.scrollTo({
          left: this.offsetLeft - parent.clientWidth / 2 + this.clientWidth / 2,
          behavior: "smooth",
        });
      } else if (parent.scrollHeight !== parent.clientHeight) {
        parent.scrollTo({
          top: this.offsetTop - parent.clientHeight / 2 + this.clientHeight / 2,
          behavior: "smooth",
        });
      }
    };

    onSliderUpdate = (event) => {
      if (this.type === "bullet") {
        const targetIndex =
          parseInt(this.actionModifier.split(":").at(-1), 10) - 1;

        if (event.detail.index === targetIndex) {
          this.setAttribute("aria-current", "true");
          if (this.shouldCenterBullet) {
            this.centerBulletInParent();
          }
        } else {
          this.setAttribute("aria-current", "false");
        }
      } else if (this.type === "button") {
        if (
          (this.target === "previous" &&
            !this.slider.loop &&
            this.slider.nearInlineStart) ||
          (this.target === "next" &&
            !this.slider.loop &&
            this.slider.nearInlineEnd)
        ) {
          this.disabled = true;
        } else {
          this.removeAttribute("disabled");
        }
      }
    };
  };
  if (!window.customElements.get("zag-slider-control")) {
    window.customElements.define(
      "zag-slider-control",
      window.zag.components.ZagSliderControl
    );
  }

  /**
   * Component for managing accordion behavior with external toggle button
   * Synchronizes the toggle button with the details/summary element
   */
  window.zag.components.ZagAccordion = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();
    }

    connectedCallback() {
      try {
        this.details = this.querySelector("details");
        this.summary = this.querySelector("summary");
        this.toggleButton = this.querySelector(".checkbox-button");

        if (!this.details || !this.summary || !this.toggleButton) {
          throw new Error(
            "Accordion must have details, summary, and toggle button elements"
          );
        }

        this.setupEventListeners();
        this.updateToggleState();
      } catch (error) {
        console.error("Error initializing ZAG accordion:", error, this);
      }
    }

    setupEventListeners = () => {
      // Listen for toggle events on the details element
      this.events.add(this.details, "toggle", this.onToggle);

      // Listen for clicks on the external toggle button
      this.events.add(this.toggleButton, "click", this.onToggleButtonClick);
    };

    onToggle = () => {
      this.updateToggleState();
    };

    onToggleButtonClick = (event) => {
      event.preventDefault();
      event.stopPropagation();

      // Toggle the details element
      if (this.details.hasAttribute("open")) {
        this.details.removeAttribute("open");
      } else {
        this.details.setAttribute("open", "");
      }
    };

    updateToggleState = () => {
      const isOpen = this.details.hasAttribute("open");

      // Update ARIA attributes for accessibility
      this.toggleButton.setAttribute("aria-expanded", isOpen);
      this.toggleButton.setAttribute(
        "aria-label",
        isOpen ? "Fermer l'accordéon" : "Ouvrir l'accordéon"
      );

      // The CSS already handles the icon visibility based on [open] attribute
      // so we don't need to manually toggle classes here
    };
  };

  if (!window.customElements.get("zag-accordion")) {
    window.customElements.define(
      "zag-accordion",
      window.zag.components.ZagAccordion
    );
  }

  // Initialize `zag` when the DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", window.zag.init);
  } else {
    window.zag.init();
  }
})();
